import type { ReactNode } from 'react';
import { Button } from './Button';
import { Icon } from './Icon';
import './PageHeader.scss';

interface PageHeaderActionsProps {
  /** 'view' shows Edit, 'edit' shows Cancel and Save, none shows only `children`. */
  mode?: 'view' | 'edit';
  onEdit?: () => void;
  onSave?: () => void;
  onCancel?: () => void;
  /** Disables Edit (e.g. while the record loads). */
  canEdit?: boolean;
  /** Disables Save (e.g. nothing changed or the form is invalid). */
  canSave?: boolean;
  /** Saving: both buttons are disabled so it cannot be sent twice. */
  busy?: boolean;
  editLabel?: string;
  /**
   * "Always editable" pages (2 Nov design review): there is no Edit step. Pass whether the form differs from what was
   * saved; while it does, the header shows an "Unsaved changes" hint with Discard and Save, otherwise only `children`.
   * Used instead of `mode`.
   */
  dirty?: boolean;
  onDiscard?: () => void;
  /**
   * Keeps Discard and Save visible even while `dirty` is false, instead of only once something changes - for a page
   * whose approved design wants a persistent action bar rather than one that appears on the first change. The
   * "Unsaved changes" hint still only shows while actually `dirty`.
   */
  alwaysShowActions?: boolean;
}

/**
 * The Edit button (`mode="view"`) - always rendered in the header itself, never in `PageHeaderFooter`: it is how a
 * `mode`-toggle page starts editing in the first place, so a sticky-bottom footer has nothing to show until then.
 */
function renderEditButton({ mode, onEdit, canEdit = true, editLabel = 'Edit' }: PageHeaderActionsProps) {
  return (
    mode === 'view' && (
      <Button className="action-button primary-button btn-edit" disabled={!canEdit} onClick={onEdit}>
        <Icon name="edit" outlined />
        <span>{editLabel}</span>
      </Button>
    )
  );
}

/** The Discard/Save or Cancel/Save pair - shared between the header bar and the sticky footer bar, per `actionsPlacement`. */
function renderSaveActions({
  mode,
  onSave,
  onCancel,
  canSave = true,
  busy = false,
  dirty,
  onDiscard,
  alwaysShowActions,
}: PageHeaderActionsProps) {
  return (
    <>
      {(dirty || alwaysShowActions) && (
        <>
          {dirty && (
            <span className="kit-page-header__dirty" role="status">
              Unsaved changes
            </span>
          )}
          <Button className="action-button secondary-button" disabled={busy} onClick={onDiscard}>
            Discard
          </Button>
          <Button className="action-button primary-button" disabled={busy || !canSave} onClick={onSave}>
            <Icon name="save" outlined />
            <span>Save</span>
          </Button>
        </>
      )}
      {mode === 'edit' && (
        <>
          <Button className="action-button secondary-button" disabled={busy} onClick={onCancel}>
            Cancel
          </Button>
          <Button className="action-button primary-button" disabled={busy || !canSave} onClick={onSave}>
            <Icon name="save" outlined />
            <span>Save</span>
          </Button>
        </>
      )}
    </>
  );
}

interface PageHeaderProps extends PageHeaderActionsProps {
  title: ReactNode;
  subtitle?: ReactNode;
  /**
   * Hides the title/subtitle visually (e.g. a page whose left-hand menu already names it, so a second heading would
   * just repeat the same words) while keeping the header row - `children` (an info icon, a close button, ...) still
   * renders, and the title stays in the DOM (`cdk-visually-hidden`) as the page's accessible name.
   */
  hideTitle?: boolean;
  /**
   * 'header' (the default) keeps Discard/Save (or Cancel/Save, or Edit) inline in this sticky top bar, next to
   * `children`. 'footer' leaves them out of the header entirely - render a `<PageHeaderFooter>` yourself as the very
   * last element of the page's content instead, for a page whose approved design wants a persistent action bar fixed
   * to the bottom of the viewport rather than the top. Pass it the same action-related props as this component.
   */
  actionsPlacement?: 'header' | 'footer';
  /** Extra actions shown before the buttons, e.g. a "Reset to default" link or an info hint. */
  children?: ReactNode;
}

/**
 * Design review 2 Nov 2026: one page header, title on the left and the page's actions on the right, which stays at the top
 * of the scrolling area so Edit/Save are always in view - replacing the fixed footer (`EditSaveActions`). Put it as the
 * first child of the scrolling area (`.form`), or sticking has nothing to stick to.
 *
 * Design review 4 Nov 2026: a page can instead ask for its actions in a persistent `PageHeaderFooter` fixed to the
 * bottom of the viewport (`actionsPlacement="footer"`) - every other consumer is unaffected, since the default stays
 * the header placement above.
 */
export function PageHeader({
  title,
  subtitle,
  hideTitle = false,
  actionsPlacement = 'header',
  children,
  ...actionsProps
}: PageHeaderProps) {
  return (
    <header className={`kit-page-header${hideTitle ? ' kit-page-header--compact' : ''}`}>
      <div className="kit-page-header__text">
        <h2 className={`kit-page-header__title${hideTitle ? ' cdk-visually-hidden' : ''}`}>{title}</h2>
        {!hideTitle && subtitle && <p className="kit-page-header__subtitle">{subtitle}</p>}
      </div>
      <div className="kit-page-header__actions">
        {children}
        {renderEditButton(actionsProps)}
        {actionsPlacement === 'header' && renderSaveActions(actionsProps)}
      </div>
    </header>
  );
}

/**
 * The Discard/Save (or Cancel/Save) pair for a page using `<PageHeader actionsPlacement="footer">`, pinned to the
 * bottom of the viewport instead of the top. Place it as the LAST element of the page's content (a sibling after
 * everything else, not inside `PageHeader`), so its sticky position has the whole page to stick within. There is
 * nothing to show in `mode="view"` (nothing is being edited yet) - that Edit button always stays in the header, see
 * `PageHeader` itself.
 */
export function PageHeaderFooter({ mode, dirty, alwaysShowActions, ...rest }: PageHeaderActionsProps) {
  if (!dirty && !alwaysShowActions && mode !== 'edit') return null;
  return (
    <footer className="kit-page-header-footer">
      {renderSaveActions({ mode, dirty, alwaysShowActions, ...rest })}
    </footer>
  );
}
